import type { Permission, Stage } from '@/domain';
import { useSession } from '@/application/usecases/useSession';

/**
 * What can be done to a selection of cases. Section 4 of the request of
 * 24 September 2026: "delete all, archive all, assign all to a particular
 * department or person", and taking archived cases out again.
 */
export type SelectionAction = 'assign' | 'archive' | 'unarchive' | 'delete';

/** The permission behind each, which the server checks again. */
const NEEDS: Record<SelectionAction, Permission> = {
  assign: 'assign',
  archive: 'archiveCase',
  unarchive: 'archiveCase',
  delete: 'deleteCase',
};

/**
 * The actions this reader may take, of the ones a screen offers. A screen that
 * is left with none draws no boxes at all: a box that can do nothing is a
 * question with no answer.
 */
export function usePermittedActions(offered: readonly SelectionAction[]): SelectionAction[] {
  const { data } = useSession();
  const held = data?.permissions ?? [];
  return offered.filter((action) => held.includes(NEEDS[action]));
}

/** The one department every case is at, or null when they are at several or at none. */
export function commonStageOf(stages: readonly (Stage | null)[]): Stage | null {
  const first = stages[0] ?? null;
  return first !== null && stages.every((stage) => stage === first) ? first : null;
}
